import { useEffect, useState } from 'react'
import { api } from '../../services/api.js'
import UpgradeInline from '../shared/UpgradeInline.jsx'

/**
 * Archive matches — shows candidate records from the Knowledge Graph
 * (external_records table) that plausibly correspond to this person.
 *
 * Endpoint: GET /api/records/match/:personId
 *   Returns: { items, tier, tier_limit, has_more, person }
 *
 * The endpoint's matching heuristic (see routes/records.js) is:
 *   surname exact + birth_year within ±5 years + given-name prefix.
 * Tier gates the row count (Free 3, Plus 20, Family/Archive 100). When
 * the API truncates results, `has_more` is true and this panel surfaces
 * an UpgradeInline CTA with the correct target plan.
 *
 * Rendered on PersonPage; skipped on public person pages / when the
 * caller has no access (endpoint 403s, we silently show nothing).
 */
const RECORD_TYPE_LABELS = {
  birth:    'Birth',
  baptism:  'Baptism',
  marriage: 'Marriage',
  death:    'Death',
  burial:   'Burial',
  census:   'Census',
  military: 'Military',
  indi:     'Record',
  other:    'Record',
}

function fmtYear(y) { return y ? String(y) : '' }
function fmtDate(d) {
  if (!d) return null
  const s = String(d)
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  if (/^\d{4}-\d{2}$/.test(s)) return s
  if (/^\d{4}$/.test(s)) return s
  return s
}
function fullName(m) {
  return [m.first_name, m.patronymic, m.last_name].filter(Boolean).join(' ')
}

export default function ArchiveMatchesPanel({ personId }) {
  const [state, setState] = useState({ loading: true, items: [], meta: null, err: null })

  useEffect(() => {
    let cancelled = false
    setState(s => ({ ...s, loading: true, err: null }))
    api.get(`/records/match/${personId}`)
      .then(data => { if (!cancelled) setState({ loading: false, items: data.items || [], meta: data, err: null }) })
      .catch(err => {
        if (cancelled) return
        // 403 (no access) is not an error worth showing — happens on
        // public pages or when the person isn't in the user's tree.
        if (err.status === 403) setState({ loading: false, items: [], meta: null, err: null })
        else setState({ loading: false, items: [], meta: null, err })
      })
    return () => { cancelled = true }
  }, [personId])

  const { loading, items, meta, err } = state

  // Silence on 403 / auth failures so the panel simply disappears.
  if (!loading && !err && items.length === 0 && !meta) return null

  return (
    <div className="ai-output" style={{ marginTop: '1.5rem' }}>
      <div className="ai-output__header">
        <span className="ai-output__label">Matches in the Knowledge Graph</span>
      </div>

      <div className="ai-output__body" style={{ padding: 0 }}>
        {loading && (
          <div style={{ padding: '1rem', color: 'var(--text-muted)', fontSize: '0.88rem' }}>
            Searching archives…
          </div>
        )}

        {!loading && items.length === 0 && (
          <div style={{ padding: '1rem', color: 'var(--text-muted)', fontSize: '0.88rem' }}>
            No matches yet. As more family trees are ingested into the shared archive, candidates for this person may appear here.
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="archive-matches">
            {items.map(m => (
              <div key={m.id} className="archive-matches__row">
                <div className="archive-matches__body">
                  <div className="archive-matches__name">
                    {fullName(m) || <em style={{ color: 'var(--text-muted)' }}>Unnamed record</em>}
                  </div>
                  <div className="archive-matches__meta">
                    <span className="archive-matches__type">
                      {RECORD_TYPE_LABELS[m.record_type] || 'Record'}
                    </span>
                    {(m.birth_year || m.birth_date) && (
                      <span>· b. {fmtDate(m.birth_date) || fmtYear(m.birth_year)}</span>
                    )}
                    {(m.death_year || m.death_date) && (
                      <span>· d. {fmtDate(m.death_date) || fmtYear(m.death_year)}</span>
                    )}
                    {m.birth_place && <span>· {m.birth_place}</span>}
                  </div>
                  {m.source_archive && (
                    <div className="archive-matches__source">
                      {m.source_archive}
                      {m.source_collection ? ` · ${m.source_collection}` : ''}
                    </div>
                  )}
                </div>
                {m.source_url && (
                  <a
                    href={m.source_url}
                    className="btn btn-ghost btn-sm"
                    target="_blank"
                    rel="noopener noreferrer"
                  >View source ↗</a>
                )}
              </div>
            ))}

            {meta?.has_more && (
              <div style={{ padding: '0.75rem 1rem 0' }}>
                <UpgradeInline
                  message={`Showing ${meta.tier_limit} of ${meta.tier_limit}+ matches — ${meta.tier === 'free' ? 'upgrade to Plus to see 20' : 'upgrade to Researcher for unlimited'} results.`}
                  suggestedPlan={meta.tier === 'free' ? 'plus' : 'researcher'}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
